/**
 * Multi-instance tracking for selected case factors when count > 1.
 * Instances map to Service History employment rows (member_id / company).
 */

export const INSTANCE_TRACKED_FACTOR_KEYS = [
  'jd_issues',
  'exit_date',
  'transfers',
  'advance_claims',
  'eps_merging'
];

export const emptyFactorInstances = () => {
  const out = {};
  INSTANCE_TRACKED_FACTOR_KEYS.forEach((key) => {
    out[key] = [];
  });
  return out;
};

export const isInstanceTrackedFactor = (factorKey) =>
  INSTANCE_TRACKED_FACTOR_KEYS.includes(factorKey);

/** Stable key for a service-history employment row */
export const serviceHistoryRowKey = (row = {}, index = 0) => {
  if (row._instanceKey) return String(row._instanceKey);
  const parts = [
    String(row.member_id || '').trim(),
    String(row.company_name || '').trim().toLowerCase(),
    String(row.doj || '').trim(),
    String(row.dol || '').trim()
  ];
  const joined = parts.join('|');
  return joined.replace(/\|/g, '') ? joined : `row-${index}`;
};

export const formatServiceHistoryLabel = (row = {}) => {
  const company = String(row.company_name || '').trim() || 'Unknown company';
  const id = String(row.member_id || '').trim();
  const doj = String(row.doj || '').trim();
  const dol = String(row.dol || '').trim();
  const dates = doj || dol ? `${doj || '—'} → ${dol || '—'}` : '';
  if (id && dates) return `${company} · ID ${id} · ${dates}`;
  if (id) return `${company} · ID ${id}`;
  if (dates) return `${company} · ${dates}`;
  return company;
};

export const rollupInstanceStatuses = (instances = []) => {
  if (!instances.length) return 'Not Started';
  const values = instances.map((i) => i.status || 'Not Started');
  if (values.every((s) => s === 'Not Started')) return 'Not Started';
  if (values.every((s) => s === 'Completed')) return 'Completed';
  if (values.some((s) => s === 'Pending with Employer/PF Office')) {
    return 'Pending with Employer/PF Office';
  }
  return 'Work in Progress';
};

/**
 * Keep instances aligned with count + selected service-history keys.
 * count <= 0 → clear instances.
 * count >= 1 → keep selected keys (used for Form-13 auto Transfers, etc.)
 */
export const syncFactorInstancesForKey = ({
  count,
  selectedKeys = [],
  serviceHistory = [],
  previousInstances = []
}) => {
  const qty = Math.max(0, parseFloat(count) || 0);
  if (qty <= 0) return [];

  const prevByKey = {};
  (previousInstances || []).forEach((inst) => {
    if (inst?.key) prevByKey[inst.key] = inst;
  });

  const historyByKey = {};
  (serviceHistory || []).forEach((row, index) => {
    const key = serviceHistoryRowKey(row, index);
    historyByKey[key] = { row, index };
  });

  return (selectedKeys || [])
    .filter((key) => historyByKey[key] || prevByKey[key])
    .slice(0, qty)
    .map((key) => {
      const fromHistory = historyByKey[key];
      const prev = prevByKey[key];
      const row = fromHistory?.row || {
        member_id: prev?.member_id,
        company_name: prev?.company_name,
        doj: prev?.doj,
        dol: prev?.dol
      };
      return {
        key,
        member_id: row.member_id || '',
        company_name: row.company_name || '',
        doj: row.doj || '',
        dol: row.dol || '',
        label: formatServiceHistoryLabel(row),
        status: prev?.status || 'Not Started'
      };
    });
};

export const toggleInstanceSelection = (selectedKeys, key, maxCount) => {
  const set = new Set(selectedKeys || []);
  if (set.has(key)) {
    set.delete(key);
  } else if (set.size < maxCount) {
    set.add(key);
  }
  return Array.from(set);
};

/** Build flat activity lines for chat / comparison */
export const flattenActivityLines = ({
  requirements = [],
  statuses = {},
  requirementStatusField = {},
  factorInstances = {},
  factorKeyByRequirement = {}
}) => {
  const lines = [];

  requirements.forEach((req) => {
    const field = requirementStatusField[req];
    const factorKey = factorKeyByRequirement[req];
    const instances = factorKey ? (factorInstances[factorKey] || []) : [];

    if (instances.length >= 1) {
      instances.forEach((inst) => {
        lines.push({
          id: `${factorKey}:${inst.key}`,
          label: `${req} · ${inst.label || inst.company_name || inst.member_id || 'Instance'}`,
          status: inst.status || 'Not Started',
          isInstance: true
        });
      });
    } else if (field) {
      lines.push({
        id: field,
        label: req,
        status: statuses[field] || 'Not Started',
        isInstance: false
      });
    }
  });

  return lines;
};
